import photoAssets from './photoAssets.json';

const localPhoto = (category, name, alt) => ({
  ...photoAssets[`${category}/${name}`],
  alt
});

// Shuffle once when the site opens; React updates keep the same photo order.
const shuffled = images => {
  const result = [...images];
  for (let index = result.length - 1; index > 0; index--) {
    const other = Math.floor(Math.random() * (index + 1));
    [result[index], result[other]] = [result[other], result[index]];
  }
  return result;
};

const headshotSubjects = [
  ['F78A9576', 'Smiling professional in a dark suit and blue patterned tie'],
  ['F78A3734', 'Studio portrait in a charcoal suit with folded arms'],
  ['F78A9660', 'Close-up professional headshot against a dark background'],
  ['F78A3686', 'Seated studio portrait in a pale blue suit'],
  ['F78A9569', 'Professional headshot in a dark suit and tie'],
  ['F78A3650', 'Relaxed studio portrait in a pale pink shirt'],
  ['F78A9584', 'Professional portrait in a textured navy blazer with folded arms'],
  ['F78A3699', 'Smiling studio portrait in a pale blue suit'],
  ['F78A9597', 'Relaxed professional portrait in a white shirt with folded arms'],
  ['F78A3713', 'Studio portrait in a white shirt against a warm backdrop'],
  ['F78A9585', 'Seated professional portrait in a textured navy blazer']
];
const colourHeadshots = headshotSubjects.map(([name, alt]) =>
  localPhoto('headshots', name, `${alt}, in color`));
const monochromeHeadshots = shuffled(headshotSubjects).slice(0, 4).map(([name, alt]) =>
  localPhoto('headshots', `${name}_bw`, `${alt}, in black and white`));
const mixedHeadshots = colourHeadshots.flatMap((image, index) =>
  index % 3 === 1 ? [image, monochromeHeadshots[Math.floor(index / 3)]] : [image]);

const photo = (id, alt) => ({
  src: `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=800&q=75`,
  srcSet: [480, 800, 1200, 1600].map(width =>
    `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${width}&q=75 ${width}w`
  ).join(', '),
  alt
});

// Collections without supplied photographs still use sample imagery.
const workCategories = [
  {
    slug: 'newborn', title: 'Newborn',
    description: 'Tiny details, soft light, and the beauty of a new beginning.',
    isSample: false,
    layout: 'editorial',
    galleryStyle: 'aligned',
    galleryRows: [3, 2, 2],
    cover: localPhoto('newborn', '1', 'Sleeping newborn wrapped in pink with a bow headband'),
    images: shuffled([
      localPhoto('newborn', '1', 'Sleeping newborn wrapped in pink with a bow headband'),
      localPhoto('newborn', '2', 'Sleeping newborn in a blue wrap and knitted bear bonnet'),
      localPhoto('newborn', '3', 'Newborn in a peach wrap resting in a wooden basket with flowers'),
      localPhoto('newborn', '4', 'Close-up of a sleeping newborn in a soft blue bonnet'),
      localPhoto('newborn', '5', 'Sleeping newborn resting on their hands on a plum blanket'),
      localPhoto('newborn', '10', 'Sleeping newborn resting on their hands on a green blanket'),
      localPhoto('newborn', 'IMG_2994', 'Newborn curled on a white blanket with a delicate lace headband')
    ])
  },
  {
    slug: 'wedding', title: 'Wedding',
    description: 'Honest emotion, beautiful details, and moments worth keeping forever.',
    isSample: false,
    layout: 'editorial',
    galleryStyle: 'aligned',
    galleryRows: [3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 2, 2],
    cover: localPhoto('wedding', '1L0A3595', 'Bride holding white bridal sandals beside her wedding gown'),
    images: shuffled([
      localPhoto('wedding', '1L0A3595', 'Bride holding white bridal sandals beside her wedding gown'),
      localPhoto('wedding', 'DSC04935', 'Bride and groom beside a white sports car outside their wedding venue'),
      localPhoto('wedding', 'DSC04110', 'Bride holding a bouquet beside tall sunlit windows'),
      localPhoto('wedding', 'DSC04167', 'Bride in a white gown on a wooden staircase'),
      localPhoto('wedding', 'DSC04395', 'Wedding couple walking together through a garden'),
      localPhoto('wedding', 'DSC04565', 'Newlyweds celebrating with their wedding party outside the venue'),
      localPhoto('wedding', 'DSC04776', 'Close-up of newlyweds kissing beneath a delicate veil'),
      localPhoto('wedding', 'DSC04782', 'Wedding couple sharing a kiss under the bridal veil'),
      localPhoto('wedding', 'DSC04794', 'Bride in profile beneath a translucent veil'),
      localPhoto('wedding', 'DSC04808', 'Newlyweds smiling together beneath the bridal veil'),
      localPhoto('wedding', 'DSC04838', 'Wedding couple seated together outside their venue'),
      localPhoto('wedding', 'DSC04855', 'Newlyweds seated in a blue convertible on a garden drive'),
      localPhoto('wedding', 'DSC04877', 'Wedding couple kissing in a blue convertible'),
      localPhoto('wedding', 'DSC03657', 'Groom and groomsmen in blue suits beside a white car'),
      localPhoto('wedding', 'DSC03887', 'Wedding guests posing together outside the venue'),
      localPhoto('wedding', 'DSC04527', 'Wedding party posing together in a green garden'),
      localPhoto('wedding', '1L0A6095', 'Bride seated on a bench beneath a spreading tree'),
      localPhoto('wedding', '1L0A6112', 'Bride and groom beneath a large tree with the veil lifted'),
      localPhoto('wedding', '1L0A6769', 'Seated bride holding a bouquet in soft outdoor light'),
      localPhoto('wedding', '1L0A6839', 'Bride and groom sharing a quiet moment beside a garden bench'),
      localPhoto('wedding', '1L0A6898', 'Wedding couple standing on coastal rocks above the sea'),
      localPhoto('wedding', '1L0A6944', 'Silhouette of newlyweds embracing beside the sea at sunset'),
      localPhoto('wedding', '1L0A7153', 'Bride and groom walking hand in hand along the beach at sunset'),
      localPhoto('wedding', '1L0A7535', 'Newlyweds touching foreheads in warm evening light'),
      localPhoto('wedding', '1L0A7537', 'Close silhouette of newlyweds together at sunset'),
      localPhoto('wedding', '0J7A8675', 'Bride wearing traditional red wedding attire and gold jewelry'),
      localPhoto('wedding', '0J7A8891', 'Newlyweds celebrating outdoors in traditional wedding attire'),
      localPhoto('wedding', '0J7A8926', 'Wedding couple wearing red and green floral garlands'),
      localPhoto('wedding', '0J7A9131', 'Bride and groom smiling together during a traditional ceremony'),
      localPhoto('wedding', '0J7A4273', 'Newlyweds celebrating above a car decorated with a Just Married banner'),
      localPhoto('wedding', 'R6_12200', 'Bride and groom walking hand in hand outside a modern wedding venue'),
      localPhoto('wedding', 'R6__2442', 'Bride and bridesmaids laughing together with their bouquets'),
      localPhoto('wedding', 'R6__2647', 'Wedding party gathered outside a country venue'),
      localPhoto('wedding', 'R6__2767', 'Bride and groom together beside a reflective window')
    ])
  },
  {
    slug: 'portraits', title: 'Portraits',
    description: 'Character and expression, finished with a gentle, natural touch.',
    images: [
      photo('1534528741775-53994a69daeb', 'Expressive portrait in natural light'),
      photo('1524504388940-b1c1722653e1', 'Outdoor portrait with soft tones'),
      photo('1506794778202-cad84cf45f1d', 'Classic male portrait'),
      photo('1500648767791-00dcc994a43e', 'Relaxed portrait in daylight'),
      photo('1526510747491-58f928ec870f', 'Thoughtful editorial portrait'),
      photo('1531123897727-8f129e1688ce', 'Natural beauty portrait')
    ]
  },
  {
    slug: 'headshots', title: 'Headshots',
    description: 'Confident, approachable portraits that still feel completely like you.',
    isSample: false,
    layout: 'editorial',
    galleryStyle: 'aligned',
    galleryRows: [3, 3, 3, 3, 3],
    cover: colourHeadshots[0],
    images: mixedHeadshots
  },
  {
    slug: 'family', title: 'Family',
    description: 'Warm connections and everyday moments, preserved with care.',
    isSample: false,
    layout: 'editorial',
    galleryStyle: 'family-story',
    galleryRows: [2, 3, 2, 1, 3, 2, 3, 2, 3, 2],
    cover: localPhoto('family', 'DSC_0792', 'Extended family gathered outdoors in warm evening light'),
    images: [
      localPhoto('family', 'DSC_0792', 'Extended family gathered outdoors in warm evening light'),
      localPhoto('family', 'DSC_0385', 'Close family portrait in warm autumn light'),
      localPhoto('family', 'DSC_0691', 'Baby sitting on a soft blanket beside a parent'),
      localPhoto('family', 'DSC_0368', 'Family of four beside a mountain lake'),
      localPhoto('family', 'DSC_0381', 'Family of four beneath autumn trees overlooking the mountains'),
      localPhoto('family', 'DSC_1347', 'Couple standing together in autumn grass'),
      localPhoto('family', 'DSC_1283', 'Parents and children sharing a close family portrait'),
      localPhoto('family', 'DSC_0731', 'Extended family gathered on a hillside at sunset'),
      localPhoto('family', 'DSC_0819', 'Grandmother and children sharing a candid moment'),
      localPhoto('family', 'DSC_0835', 'Grandmother holding a baby surrounded by children'),
      localPhoto('family', 'DSC_1549', 'Grandmother and family with a baby on a white bench'),
      localPhoto('family', 'DSC_1272', 'Parents seated with their children on a white bench'),
      localPhoto('family', 'DSC_1360', 'Smiling couple sharing an embrace outdoors'),
      localPhoto('family', 'DSC_0759', 'Generations of a family gathered on a hillside'),
      localPhoto('family', 'DSC_0764', 'Extended family enjoying a moment together outdoors'),
      localPhoto('family', 'DSC_0787', 'Extended family portrait beneath an open evening sky'),
      localPhoto('family', 'DSC_1556', 'Three generations together on a garden bench'),
      localPhoto('family', 'DSC_1286', 'Family gathered around a white bench outdoors'),
      localPhoto('family', 'DSC_0742', 'Large family portrait with mountains and autumn trees'),
      localPhoto('family', 'DSC_0777', 'Family gathering against a golden mountain landscape'),
      localPhoto('family', 'DSC_0804', 'Large family together beneath autumn trees'),
      localPhoto('family', 'DSC_1000', 'Family group portrait with several generations'),
      localPhoto('family', 'DSC_1005', 'Family gathered beside a bench in the autumn grass')
    ]
  },
  {
    slug: 'celebrations', title: 'Celebrations',
    description: 'The atmosphere, the little details, and the joy of being together.',
    images: [
      photo('1511285560929-80b456fea0bc', 'A joyful celebration'),
      photo('1511795409834-ef04bbd61622', 'Guests gathering for a special occasion'),
      photo('1519671482749-fd09be7ccebf', 'Friends celebrating together'),
      photo('1464366400600-7168b8af9bc3', 'Beautifully arranged celebration table'),
      photo('1519225421980-715cb0215aed', 'An elegant event setting'),
      photo('1523438885200-e635ba2c371e', 'Thoughtful details from a special day')
    ]
  },
  {
    slug: 'real-estate', title: 'Real Estate',
    description: 'Bright spaces, balanced light, and interiors that invite a closer look.',
    images: [
      photo('1505693416388-ac5ce068fe85', 'Bedroom with soft natural light'),
      photo('1600210492486-724fe5c67fb0', 'Warm contemporary living room'),
      photo('1600607687920-4e2a09cf159d', 'Open, light-filled interior'),
      photo('1600566753086-00f18fb6b3ea', 'Modern kitchen with carefully styled details'),
      photo('1616486338812-3dadae4b4ace', 'Refined contemporary interior'),
      photo('1600047509807-ba8f99d2cdde', 'Spacious home with architectural details')
    ]
  },
  {
    slug: 'fashion', title: 'Fashion',
    description: 'Texture, tone, and editorial detail with a distinctive point of view.',
    images: [
      photo('1490481651871-ab68de25d43d', 'Fashion styling and wardrobe details'),
      photo('1483985988355-763728e1935b', 'Contemporary fashion and personal style'),
      photo('1445205170230-053b83016050', 'Fashion textures and wardrobe details'),
      photo('1485230895905-ec40ba36b9bc', 'Street-style fashion portrait'),
      photo('1539109136881-3be0616acf4b', 'Editorial fashion portrait'),
      photo('1475180098004-ca77a66827be', 'Fashion collection in natural light')
    ]
  }
];

export default workCategories;
